import { jsonrepair } from 'jsonrepair';

/**
 * Removes JavaScript-style line comments (//) from a JSON string.
 * This is useful when AI responses include comments in JSON output.
 *
 * @param jsonString - The JSON string potentially containing // comments
 * @returns Clean JSON string without line comments
 *
 * @example
 * ```typescript
 * const dirtyJson = `{
 *   "data": "value"
 *   // This is a comment
 * }`;
 * const clean = removeJSONComments(dirtyJson);
 * // Result: '{\n  "data": "value"\n  \n}'
 * ```
 */
export function removeJSONComments(jsonString: string): string {
  // Remove lines that start with // (with optional leading whitespace)
  // The 'm' flag makes ^ match the start of each line
  return jsonString.replace(/^\s*\/\/.*$/gm, '');
}

/**
 * Safely parses a JSON string that may contain // comments or be malformed.
 * Uses jsonrepair library to fix common JSON issues before parsing.
 *
 * @param jsonString - The JSON string to parse
 * @returns Parsed JSON object
 * @throws {SyntaxError} If the JSON cannot be repaired and parsed
 *
 * @example
 * ```typescript
 * const result = safeJSONParse(`{
 *   "name": "test"
 *   // comment here
 * }`);
 * // Returns: { name: "test" }
 * ```
 */
/**
 * Rescues the complete rows out of a JSON payload that was cut off mid-write.
 *
 * The agent occasionally exceeds its token budget and its answer stops in the
 * middle of an object — sometimes right after a key, as in `"data":`, which
 * jsonrepair cannot fix. Losing the whole answer over the last, partial row is a
 * poor trade: this walks the text, remembers the last position that sat on a
 * clean element boundary, cuts there and closes the structures still open.
 *
 * @param jsonString - A JSON string that failed to parse
 * @returns The salvaged object, or null when nothing usable can be recovered
 */
export function salvageTruncatedJSON<T = any>(jsonString: string): T | null {
  const stack: string[] = [];
  let inString = false;
  let escaped = false;
  // Last index (exclusive) that ended a complete array element or object member,
  // together with the structures open at that point.
  let safeEnd = -1;
  let safeStack: string[] = [];

  for (let i = 0; i < jsonString.length; i++) {
    const ch = jsonString[i];

    if (escaped) {
      escaped = false;
      continue;
    }
    if (ch === '\\') {
      if (inString) escaped = true;
      continue;
    }
    if (ch === '"') {
      inString = !inString;
      continue;
    }
    if (inString) continue;

    if (ch === '{' || ch === '[') {
      stack.push(ch);
    } else if (ch === '}' || ch === ']') {
      stack.pop();
      // Closing a nested value leaves us on an element boundary: everything up
      // to here is well-formed once the remaining structures are closed.
      if (stack.length > 0) {
        safeEnd = i + 1;
        safeStack = [...stack];
      }
    }
  }

  if (safeEnd === -1) return null;

  let candidate = jsonString.slice(0, safeEnd);
  for (let i = safeStack.length - 1; i >= 0; i--) {
    candidate += safeStack[i] === '{' ? '}' : ']';
  }

  try {
    return JSON.parse(candidate) as T;
  } catch {
    return null;
  }
}

export function safeJSONParse<T = any>(jsonString: string): T {
  try {
    // First try to remove comments
    const cleanedJSON = removeJSONComments(jsonString);
    // Then repair any malformed JSON
    const repairedJSON = jsonrepair(cleanedJSON);
    return JSON.parse(repairedJSON) as T;
  } catch (error) {
    // If repair fails, try direct repair without comment removal
    try {
      const repairedJSON = jsonrepair(jsonString);
      return JSON.parse(repairedJSON) as T;
    } catch (repairError) {
      console.error('Failed to repair JSON:', repairError);
      throw repairError;
    }
  }
}
